import geostyleOpenLayersParser from 'geostyler-openlayers-parser';
import OlStyleUtil, {DEGREES_TO_RADIANS} from './../node_modules/geostyler-openlayers-parser/dist/Util/OlStyleUtil.js';
import {
    isGeoStylerBooleanFunction,
    isGeoStylerFunction,
    isGeoStylerStringFunction,
    isIconSymbolizer,
    isMarkSymbolizer,
    isSprite
} from 'geostyler-style';
import {cleanWellKnownName, getPointSvg, isPointDefinedAsSvg, pointSvgs} from './../node_modules/geostyler-openlayers-parser/dist/Util/OlSvgPoints';
import {fillPatternSvg} from './../node_modules/geostyler-openlayers-parser/dist/Util/OlSvgPatterns';
import {
    drawSvgToCanvas,
    getDecodedSvg,
    getEncodedSvg,
    getSvgProperties,
    LINE_WELLKNOWNNAMES,
    NOFILL_WELLKNOWNNAMES
} from './../node_modules/geostyler-openlayers-parser/dist/Util/OlSvgUtil';
import {parseFont} from 'css-font-parser';
import OlImageState from 'ol/ImageState';
import OlGeomPoint from 'ol/geom/Point';
import OlStyle from 'ol/style/Style';
import OlStyleImage from 'ol/style/Image';
import OlStyleStroke from 'ol/style/Stroke';
import OlStyleText from 'ol/style/Text';
import OlStyleCircle from 'ol/style/Circle';
import OlStyleFill from 'ol/style/Fill';
import {default as OlStyleFillPattern} from './style/FillPattern.js';
import OlStyleIcon from 'ol/style/Icon';
import OlStyleRegularshape from 'ol/style/RegularShape';
import {METERS_PER_UNIT} from 'ol/proj/Units';
import {toContext} from 'ol/render';
import OlFeature from 'ol/Feature';
import {default as ScaleLine} from './control/ScaleLine.js';

function resolveAttributeTemplate(
    feature,
    template,
    noValueFoundText = 'n.v.',
    valueAdjust = (key, val) => val
) {
    const props = feature.getProperties();
    const regex = /\{\{(.*?)\}\}/g;

    return template.replace(regex, (match, attributeName) => {
        let value;

        if (props.hasOwnProperty(attributeName + '_value')) {
            value = props[attributeName + '_value'];
        } else if (props.hasOwnProperty(attributeName)) {
            value = props[attributeName];
        }

        if (value === undefined || value === null) {
            return noValueFoundText;
        }

        return valueAdjust(attributeName, value);
    });
}

function getTextFont(symbolizer, o) {
    const fontWeight = symbolizer.fontWeight ?? 'normal';
    const fontStyle = symbolizer.fontStyle ?? 'normal';
    const size = o
        ? parseFloat(o) * symbolizer.size
        : symbolizer.size;
    const font = symbolizer.font;
    return fontWeight + ' ' + fontStyle + ' ' + size + 'px ' + font
        ?.join(', ');
}

function deepCloneKeepFunctions(obj) {
    if (obj === null || typeof obj !== "object") 
        return obj;
    if (Array.isArray(obj)) 
        return obj.map(deepCloneKeepFunctions);
    
    const cloned = {};
    for (const key in obj) {
        cloned[key] = deepCloneKeepFunctions(obj[key]);
    }
    return cloned;
}

export default class OpenLayersParser extends geostyleOpenLayersParser {
    constructor(options) {
        super(options);
        this.scaleLine = new ScaleLine();
    }

    checkForUnsupportedProperties(geoStylerStyle) {
        const capitalizeFirstLetter = (a) => a[0].toUpperCase() + a.slice(1);
        const unsupportedProperties = {};
        geoStylerStyle
            .rules
            .forEach(rule => {
                // ScaleDenominator and Filters are completly supported so we just check for
                // symbolizers
                rule
                    .symbolizers
                    .forEach(symbolizer => {
                        const key = capitalizeFirstLetter(`${symbolizer.kind}Symbolizer`);
                        const value = this.unsupportedProperties
                            ?.Symbolizer
                                ?.[key];
                        if (value) {
                            if (typeof value === 'string') {
                                if (!unsupportedProperties.Symbolizer) {
                                    unsupportedProperties.Symbolizer = {};
                                }
                                unsupportedProperties.Symbolizer[key] = value;
                            } else {
                                Object
                                    .keys(symbolizer)
                                    .forEach(property => {
                                        if (value[property]) {
                                            if (!unsupportedProperties.Symbolizer) {
                                                unsupportedProperties.Symbolizer = {};
                                            }
                                            if (!unsupportedProperties.Symbolizer[key]) {
                                                unsupportedProperties.Symbolizer[key] = {};
                                            }
                                            unsupportedProperties.Symbolizer[key][property] = value[property];
                                        }
                                    });
                            }
                        }
                    });
            });
        if (Object.keys(unsupportedProperties).length > 0) {
            console.warn(
                'Unsupported GeoStyler-Style properties found:',
                unsupportedProperties
            );
            // return unsupportedProperties;
        }
        return undefined;
    }
    /**
     * Decides which OlStyleType should be returned depending on given geoStylerStyle.
     * Three OlStyleTypes are possible:
     *
     * 1. OlStyle if input Style consists of
     *    one rule with one symbolizer, no filter, no scaleDenominator, no TextSymbolizer
     * 2. OlStyle[] if input Style consists of
     *    one rule with multiple symbolizers, no filter, no scaleDenominator, no TextSymbolizer
     * 3. OlParserStyleFct for everything else
     *
     * @param geoStylerStyle A GeoStyler-Style Style
     */
    getOlStyleTypeFromGeoStylerStyle(geoStylerStyle, thismap, renderResolution) {
        const rules = geoStylerStyle.rules;
        const nrRules = rules.length;

        if (geoStylerStyle.showstyle == true) {
            return this.geoStylerStyleToOlParserStyleFct(
                geoStylerStyle,
                thismap,
                renderResolution
            );
        }

        if (nrRules === 1) {
            const hasFilter = geoStylerStyle
                ?.rules
                    ?.[0]
                        ?.filter !== undefined
                            ? true
                            : false;
            const hasMinScale = geoStylerStyle
                ?.rules
                    ?.[0]
                        ?.scaleDenominator
                            ?.min !== undefined
                                ? true
                                : false;
            const hasMaxScale = geoStylerStyle
                ?.rules
                    ?.[0]
                        ?.scaleDenominator
                            ?.max !== undefined
                                ? true
                                : false;
            const hasScaleDenominator = hasMinScale || hasMaxScale
                ? true
                : false;
            const hasFunctions = OlStyleUtil.containsGeoStylerFunctions(geoStylerStyle);
            const nrSymbolizers = geoStylerStyle
                .rules[0]
                .symbolizers
                .length;
            const hasTextSymbolizer = rules[0]
                .symbolizers
                .some((symbolizer) => {
                    return symbolizer.kind === 'Text';
                });
            const hasDynamicIconSymbolizer = rules[0]
                .symbolizers
                .some((symbolizer) => {
                    return symbolizer.kind === 'Icon' && typeof(symbolizer.image) === 'string' && symbolizer
                        .image
                        .includes('{{');
                });
            if (!hasFilter && !hasScaleDenominator && !hasTextSymbolizer && !hasDynamicIconSymbolizer && !hasFunctions) {
                if (nrSymbolizers === 1) {
                    return this.geoStylerStyleToOlStyle(geoStylerStyle, thismap, renderResolution);
                } else {
                    return this.geoStylerStyleToOlParserStyleFct(
                        geoStylerStyle,
                        thismap,
                        renderResolution
                    );
                }
            } else {
                return this.geoStylerStyleToOlParserStyleFct(
                    geoStylerStyle,
                    thismap,
                    renderResolution
                );
            }
        } else {
            return this.geoStylerStyleToOlParserStyleFct(
                geoStylerStyle,
                thismap,
                renderResolution
            );
        }
    }
    /**
     * Parses the first symbolizer of the first rule of a GeoStyler-Style Style.
     *
     * @param geoStylerStyle GeoStyler-Style Style
     * @return An OpenLayers Style Object
     */
    geoStylerStyleToOlStyle(geoStylerStyle, thismap, renderResolution) {
        const rule = geoStylerStyle.rules[0];
        const symbolizer = rule.symbolizers[0];
        const olSymbolizer = this.getOlSymbolizerFromSymbolizer(symbolizer);
        return olSymbolizer;
    }
    /**
     * Parses all symbolizers of the first rule of a GeoStyler-Style Style.
     *
     * @param geoStylerStyle GeoStyler-Style Style
     * @return An array of OpenLayers Style Objects
     */
    geoStylerStyleToOlStyleArray(geoStylerStyle) {
        const rule = geoStylerStyle.rules[0];
        const olStyles = [];
        rule
            .symbolizers
            .forEach((symbolizer) => {
                const olSymbolizer = this.getOlSymbolizerFromSymbolizer(symbolizer);
                olStyles.push(olSymbolizer);
            });
        return olStyles;
    }
    /**
     * Get the OpenLayers Style object from an GeoStyler-Style Style
     *
     * @param geoStylerStyle A GeoStyler-Style Style.
     * @return An OlParserStyleFct
     */
    geoStylerStyleToOlParserStyleFct(geoStylerStyle, thismap, renderResolution) {
        var invisibles = geoStylerStyle['invisible'];
        const rules = deepCloneKeepFunctions(geoStylerStyle.rules);
        const olStyle = (feature, resolution) => {
            var hud = {};
            var type = feature
                .getGeometry()
                .getType();
            const styles = [];
            // calculate scale for resolution (from ol-util MapUtil)
            const dpi = 25.4 / 0.28;
            const mpu = METERS_PER_UNIT.m;
            const inchesPerMeter = 39.37;
            var scale = resolution * mpu * inchesPerMeter * dpi;
            var extent = feature
                .getGeometry()
                .getExtent();
            var renderScale = renderResolution
                ? renderResolution
                : feature.get('renderScaleDefault');
            var zuruu,
                mainScaler;

            if (thismap) {
                scale = this
                    .scaleLine
                    .getScaleForResolutionV1(thismap);
            } else if (typeof map !== "undefined") {
                scale = this
                    .scaleLine
                    .getScaleForResolutionV1(map);
            }

            if (thismap) {
                mainScaler = thismap
                    .getView()
                    .get('mainScaler');
            } else if (typeof map !== "undefined") {
                mainScaler = map
                    .getView()
                    .get('mainScaler');
            }

            if (renderScale != null) {

                if (mainScaler) {

                    zuruu = (parseFloat(renderScale) * 1) / scale;

                } else {

                    zuruu = parseFloat(renderScale) / scale;

                }

            } else {
                if (mainScaler) {
                    zuruu = 1 / mainScaler;
                }
            }

            var show = false;

            rules.forEach((rule) => {
                feature.set('hidden_by_symbolizer', !show, true);

                var l2 = true;
                if (rule['visible'] !== false) {
                    if (rule['filter']) {
                        if (rule['filter'][0] == '==') {
                            rule['filter'][2] = rule['filter'][2] === null
                                ? ''
                                : rule['filter'][2];
                            var it1 = feature.get(rule['filter'][1]) === null
                                ? ''
                                : feature.get(rule['filter'][1])
                                    ? feature
                                        .get(rule['filter'][1])
                                        .toString()
                                    : '';
                            var it2 = rule['filter'][2].toString();
                            it1 = it1
                                .replace(/\r?\n|\r/g, '')
                                .replace(/ +/g, '')
                                .replaceAll(',', '.');
                            it2 = it2
                                .replace(/\r?\n|\r/g, '')
                                .replace(/ +/g, '')
                                .replaceAll(',', '.');
                            l2 = it1 == it2;

                        } else if (rule['filter'][0] == '!=') {
                            rule['filter'][2] = rule['filter'][2] === null
                                ? ''
                                : rule['filter'][2];
                            var it1 = feature.get(rule['filter'][1]) === null
                                ? ''
                                : feature.get(rule['filter'][1])
                                    ? feature
                                        .get(rule['filter'][1])
                                        .toString()
                                    : '';
                            var it2 = rule['filter'][2].toString();
                            it1 = it1
                                .replace(/\r?\n|\r/g, '')
                                .replace(/ +/g, '')
                                .replaceAll(',', '.');
                            it2 = it2
                                .replace(/\r?\n|\r/g, '')
                                .replace(/ +/g, '')
                                .replaceAll(',', '.');
                            l2 = it1 != it2;
                        }
                    }
                } else {
                    l2 = false;
                }

                if (l2) {

                    // handling scale denominator
                    let minScale = rule
                        ?.scaleDenominator
                            ?.min;
                    let maxScale = rule
                        ?.scaleDenominator
                            ?.max;

                    let scaleMax = Math.ceil(scale);
                    let scaleMin = Math.floor(scale);

                    let isWithinScale = true;
                    if (minScale || maxScale) {
                        minScale = isGeoStylerFunction(minScale)
                            ? OlStyleUtil.evaluateNumberFunction(minScale)
                            : minScale;
                        maxScale = isGeoStylerFunction(maxScale)
                            ? OlStyleUtil.evaluateNumberFunction(maxScale)
                            : maxScale;
                        if (minScale && scale < minScale) {
                            isWithinScale = false;
                        }
                        if (maxScale && scale >= maxScale) {
                            isWithinScale = false;
                        }
                    }
                    // handling filter
                    let matchesFilter = false;
                    if (!rule.filter) {
                        matchesFilter = true;
                    } else {
                        try {
                            matchesFilter = this.geoStylerFilterToOlParserFilter(feature, rule.filter);
                        } catch  {
                            matchesFilter = false;
                        }
                    }

                    var l1 = true;

                    if (rule['types']) {
                        var ruletypes = rule['types'];
                        if (!ruletypes.includes(type)) {
                            l1 = false;
                        }
                    }

                    //????
                    if (rule['symbolizers'][0]['kind'] == 'Text') {
                        if (hud[type]) {
                            var found = false;
                            $.each(hud[type], function (ind, hudty) {
                                var it1 = feature.get(ind) === null || feature.get(ind) === undefined
                                    ? ''
                                    : feature
                                        .get(ind)
                                        .toString();
                                if (hudty[it1] == false) {
                                    l1 = false;
                                }
                            });
                            if (hud[type]['other_grodan']) {
                                if (hud[type]['other_grodan']['other_grodan'] == false && show == false) {
                                    l1 = false;
                                }
                            }
                        }
                    }

                    if (l1 != false) {

                        var l3 = true;
                        if (rule['visible'] == false) {
                            l3 = false;
                            //????
                            rule
                                .symbolizers
                                .forEach(function (i) {
                                    var tp = i['kind'] == 'Mark' || i['kind'] == 'Icon'
                                        ? 'Point'
                                        : i['kind'] == 'Fill'
                                            ? 'Polygon'
                                            : i['kind'] == 'Line'
                                                ? 'LineString'
                                                : '';
                                    var tp2 = rule['filter']
                                        ? rule['filter'][0] == '&&'
                                            ? 'other_grodan'
                                            : rule['filter'][2]
                                        : '';
                                    var tp1 = rule['filter']
                                        ? rule['filter'][0] == '&&'
                                            ? 'other_grodan'
                                            : rule['filter'][1]
                                        : '';
                                    hud[tp] = hud[tp]
                                        ? hud[tp]
                                        : {};
                                    hud[tp][tp1] = hud[tp][tp1]
                                        ? hud[tp][tp1]
                                        : {};
                                    hud[tp][tp1][tp2] = false;
                                    if (tp == 'Polygon') {
                                        tp = 'MultiPolygon'
                                        hud[tp] = hud[tp]
                                            ? hud[tp]
                                            : {};
                                        hud[tp][tp1] = hud[tp][tp1]
                                            ? hud[tp][tp1]
                                            : {};
                                        hud[tp][tp1][tp2] = false;
                                    } else if (tp == 'LineString') {
                                        tp = 'MultiLineString'
                                        hud[tp] = hud[tp]
                                            ? hud[tp]
                                            : {};
                                        hud[tp][tp1] = hud[tp][tp1]
                                            ? hud[tp][tp1]
                                            : {};
                                        hud[tp][tp1][tp2] = false;
                                    }
                                });
                        } else {
                            if (invisibles != undefined) {
                                if (type == "Point" || type == "MultiPoint") {
                                    if (invisibles['Point'] == true) {
                                        l3 = false;
                                    }
                                } else if (type == "Polygon" || type == "MultiPolygon") {
                                    if (invisibles['Polygon'] == true) {
                                        l3 = false;
                                    }
                                } else if (type == "LineString" || type == "MultiLineString") {
                                    if (invisibles['Line'] == true) {
                                        l3 = false;
                                    }
                                }
                            }

                            if (l3 == true) {

                                if (isWithinScale && matchesFilter) {
                                    rule
                                        .symbolizers
                                        .forEach((symb) => {
                                            if (type == "Point" && symb.kind == "Icon" || type == "Point" && symb.kind == "Mark" || type == "Point" && symb.kind == "StyleFunction" || type == "MultiPoint" && symb.kind == "Icon" || type == "MultiPoint" && symb.kind == "Mark" || type == "MultiPoint" && symb.kind == "StyleFunction" || type == "Polygon" && symb.kind == "Fill" || type == "MultiPolygon" && symb.kind == "Fill" || type == "LineString" && symb.kind == "Line" || type == "MultiLineString" && symb.kind == "Line" || type == "LineString" && symb.kind == "StyleFunction" || type == "MultiLineString" && symb.kind == "StyleFunction" || symb.kind == "Text" || type == "Polygon" && symb.kind == "FillPattern" || type == "GeometryCollection") {

                                                var anotherbool = true;
                                                if (invisibles != undefined) {
                                                    if (invisibles['Text'] == true && symb.kind == "Text") {
                                                        anotherbool = false;
                                                    }
                                                }

                                                if (symb.kind == "Text") {
                                                    var a1 = symb
                                                            ?.MinRes,
                                                        s1 = symb
                                                            ?.MaxRes,
                                                        l01 = !0;

                                                    if (mainScaler) {
                                                        a1 = a1 * mainScaler;
                                                        s1 = s1 * mainScaler;
                                                    }

                                                    void 0 === a1 && void 0 === s1 || (
                                                        void 0 !== a1 && scaleMax < a1 && (l01 = !1),
                                                        void 0 !== s1 && scaleMin > s1 && (l01 = !1)
                                                    );

                                                    if (l01 == false) {
                                                        anotherbool = false;
                                                    }

                                                }

                                                if (symb['visible'] == false) {
                                                    anotherbool = false;
                                                    //????
                                                    var tp = symb['kind'] == 'Mark' || symb['kind'] == 'Icon'
                                                        ? 'Point'
                                                        : symb['kind'] == 'Fill'
                                                            ? 'Polygon'
                                                            : symb['kind'] == 'Line'
                                                                ? 'LineString'
                                                                : '';
                                                    var tp2 = rule['filter']
                                                        ? rule['filter'][2]
                                                        : '';
                                                    var tp1 = rule['filter']
                                                        ? rule['filter'][1]
                                                        : '';
                                                    hud[tp] = hud[tp]
                                                        ? hud[tp]
                                                        : {};
                                                    hud[tp][tp1] = hud[tp][tp1]
                                                        ? hud[tp][tp1]
                                                        : {};
                                                    hud[tp][tp1][tp2] = false;
                                                    if (tp == 'Polygon') {
                                                        tp = 'MultiPolygon'
                                                        hud[tp] = hud[tp]
                                                            ? hud[tp]
                                                            : {};
                                                        hud[tp][tp1] = hud[tp][tp1]
                                                            ? hud[tp][tp1]
                                                            : {};
                                                        hud[tp][tp1][tp2] = false;
                                                    } else if (tp == 'LineString') {
                                                        tp = 'MultiLineString'
                                                        hud[tp] = hud[tp]
                                                            ? hud[tp]
                                                            : {};
                                                        hud[tp][tp1] = hud[tp][tp1]
                                                            ? hud[tp][tp1]
                                                            : {};
                                                        hud[tp][tp1][tp2] = false;
                                                    }
                                                }

                                                if (anotherbool == true) {

                                                    if (symb.visibility === false) {
                                                        styles.push(null);
                                                    }
                                                    if (isGeoStylerBooleanFunction(symb.visibility)) {
                                                        const visibility = OlStyleUtil.evaluateBooleanFunction(symb.visibility);
                                                        if (!visibility) {
                                                            styles.push(null);
                                                        }
                                                    }

                                                    const olSymbolizer = this.getOlSymbolizerFromSymbolizer(
                                                        symb,
                                                        feature,
                                                        zuruu,
                                                        feature.getProperties()
                                                    );

                                                    show = true;

                                                    //infinity loop detected
                                                    feature.set('hidden_by_symbolizer', !show, true);

                                                    // either an OlStyle or an ol.StyleFunction. OpenLayers only accepts an array of
                                                    // OlStyles, not ol.StyleFunctions. So we have to check it and in case of an
                                                    // ol.StyleFunction call that function and add the returned style to const
                                                    // styles.
                                                    if (typeof olSymbolizer !== 'function') {
                                                        styles.push(olSymbolizer);
                                                    } else {
                                                        const styleFromFct = olSymbolizer(feature, resolution);
                                                        styles.push(styleFromFct);
                                                    }
                                                }
                                            }
                                        });
                                }
                            }
                        }
                    }
                }
            });
            return styles;
        };
        const olStyleFct = olStyle;
        olStyleFct.__geoStylerStyle = geoStylerStyle;
        return olStyleFct;
    }
    /**
     * Checks if a feature matches given filter expression(s)
     * @param feature ol.Feature
     * @param filter Filter
     * @return boolean true if feature matches filter expression
     */
    geoStylerFilterToOlParserFilter(feature, filter) {
        const operatorMapping = {
            '&&': true,
            '||': true,
            '!': true
        };
        let matchesFilter = true;
        if (isGeoStylerBooleanFunction(filter)) {
            return OlStyleUtil.evaluateBooleanFunction(filter, feature);
        }
        if (filter === true || filter === false) {
            return filter;
        }
        const operator = filter[0];
        let isNestedFilter = false;
        if (operatorMapping[operator]) {
            isNestedFilter = true;
        }
        try {
            if (isNestedFilter) {
                let intermediate;
                let restFilter;
                switch (filter[0]) {
                    case '&&':
                        intermediate = true;
                        restFilter = filter.slice(1);
                        restFilter.forEach((f) => {
                            if (!this.geoStylerFilterToOlParserFilter(feature, f)) {
                                intermediate = false;
                            }
                        });
                        matchesFilter = intermediate;
                        break;
                    case '||':
                        intermediate = false;
                        restFilter = filter.slice(1);
                        restFilter.forEach((f) => {
                            if (this.geoStylerFilterToOlParserFilter(feature, f)) {
                                intermediate = true;
                            }
                        });
                        matchesFilter = intermediate;
                        break;
                    case '!':
                        matchesFilter = !this.geoStylerFilterToOlParserFilter(feature, filter[1]);
                        break;
                    default:
                        throw new Error(
                            'Cannot parse Filter. Unknown combination or negation operator.'
                        );
                }
            } else {
                let arg1;
                if (isGeoStylerFunction(filter[1])) {
                    arg1 = OlStyleUtil.evaluateFunction(filter[1], feature);
                } else {
                    arg1 = feature.get(filter[1]);
                }
                let arg2;
                if (isGeoStylerFunction(filter[2])) {
                    arg2 = OlStyleUtil.evaluateFunction(filter[2], feature);
                } else {
                    arg2 = filter[2];
                }
                switch (filter[0]) {
                    case '==':
                        // matchesFilter = ('' + arg1) === ('' + arg2);
                        matchesFilter = ('' + arg1).replaceAll(',', '.') === ('' + arg2).replaceAll(
                            ',',
                            '.'
                        );
                        break;
                    case '*=':
                        // inspired by
                        // https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/String/includes#Polyfill
                        if (typeof arg2 === 'string' && typeof arg1 === 'string') {
                            if (arg2.length > arg1.length) {
                                matchesFilter = false;
                            } else {
                                matchesFilter = arg1.indexOf(arg2) !== -1;
                            }
                        }
                        break;
                    case '!=':
                        if (arg1 != null) {
                            // matchesFilter = ('' + arg1) !== ('' + arg2);
                            matchesFilter = ('' + arg1).replaceAll(',', '.') !== ('' + arg2).replaceAll(
                                ',',
                                '.'
                            );
                        }
                        break;
                    case '<':
                        matchesFilter = Number(arg1) < Number(arg2);
                        break;
                    case '<=':
                        matchesFilter = Number(arg1) <= Number(arg2);
                        break;
                    case '>':
                        matchesFilter = Number(arg1) > Number(arg2);
                        break;
                    case '>=':
                        matchesFilter = Number(arg1) >= Number(arg2);
                        break;
                    default:
                        throw new Error('Cannot parse Filter. Unknown comparison operator.');
                }
            }
        } catch  {
            throw new Error('Cannot parse Filter. Invalid structure.');
        }
        return matchesFilter;
    }
    /**
     * Get the OpenLayers Style object or an OL StyleFunction from an
     * GeoStyler-Style Symbolizer.
     *
     * @param symbolizer A GeoStyler-Style Symbolizer.
     * @return The OpenLayers Style object or a StyleFunction
     */
    getOlSymbolizerFromSymbolizer(symbolizer, feature, zuruu, props) {
        let olSymbolizer;
        const clonedSymbolizer = deepCloneKeepFunctions(symbolizer);
        // var clonedSymbolizer = structuredClone(symbolizer);
        switch (symbolizer.kind) {
            case 'Mark':
                olSymbolizer = this.getOlPointSymbolizerFromMarkSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'Icon':
                olSymbolizer = this.getOlIconSymbolizerFromIconSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'Text':
                olSymbolizer = this.getOlTextSymbolizerFromTextSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'Line':
                olSymbolizer = this.getOlLineSymbolizerFromLineSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'Fill':
                olSymbolizer = this.getOlPolygonSymbolizerFromFillSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'FillPattern':
                olSymbolizer = this.getOlPolygonSymbolizerFromFillPatternSymbolizer(
                    clonedSymbolizer,
                    feature,
                    zuruu,
                    props
                );
                break;
            case 'StyleFunction':
                olSymbolizer = clonedSymbolizer.style;
                break;
            default:
                {
                    // Return the OL default style since the TS type binding does not allow us to
                    // set olSymbolizer to undefined
                    const fill = new this.OlStyleFillConstructor({color: 'rgba(255,255,255,0.4)'});
                    const stroke = new this.OlStyleStrokeConstructor(
                        {color: '#3399CC', width: 1.25}
                    );
                    olSymbolizer = new this.OlStyleConstructor({
                        image: new this.OlStyleCircleConstructor(
                            {fill: fill, stroke: stroke, radius: 5}
                        ),
                        fill: fill,
                        stroke: stroke
                    });
                    break;
                }
        }
        return olSymbolizer;
    }
    /**
     * Get the OL Style object from an GeoStyler-Style MarkSymbolizer.
     *
     * @param markSymbolizer A GeoStyler-Style MarkSymbolizer.
     * @return The OL Style object
     */
    getOlPointSymbolizerFromMarkSymbolizer(markSymbolizer, feature, o, props) {
        for (const key of Object.keys(markSymbolizer)) {
            if (isGeoStylerFunction(markSymbolizer[key])) {
                markSymbolizer[key] = OlStyleUtil.evaluateFunction(
                    markSymbolizer[key],
                    feature
                );
            }
        }

        var myrotate = markSymbolizer.rotate;
        if (isNaN(markSymbolizer.rotate)) {
            myrotate = props
                ? props[markSymbolizer.rotate + '_asrotate']
                : markSymbolizer.rotate;
            if (props) {
                if (props[markSymbolizer.rotate + '_asrotate']) {
                    myrotate = props[markSymbolizer.rotate + '_asrotate'];
                } else if (props[markSymbolizer.rotate + '_value']) {
                    myrotate = props[markSymbolizer.rotate + '_value'];
                }
            }
        }

        const strokeColor = markSymbolizer.strokeColor;
        const strokeOpacity = markSymbolizer.strokeOpacity;
        const strokeWidth = o
            ? parseFloat(o) * markSymbolizer.strokeWidth
            : markSymbolizer.strokeWidth;
        const fillColor = markSymbolizer.color;
        const fillOpacity = markSymbolizer.opacity ?? markSymbolizer.fillOpacity;
        const rotation = parseFloat(myrotate)
            ? parseFloat(myrotate) * DEGREES_TO_RADIANS
            : 0;
        const displacement = markSymbolizer.offset;
        const scale = o
            ? parseFloat(o) * markSymbolizer.radius / 7
            : markSymbolizer.radius / 7;
        let olStyle;

        if (isPointDefinedAsSvg(markSymbolizer.wellKnownName)) {
            const opacity = markSymbolizer.opacity ?? markSymbolizer.fillOpacity;
            const svg = getPointSvg(markSymbolizer);
            olStyle = new this.OlStyleConstructor({
                image: new this.OlStyleIconConstructor({
                    crossOrigin: 'anonymous',
                    ...displacement && {
                        displacement
                    },
                    ...OlStyleUtil.checkOpacity(opacity) && {
                        opacity
                    },
                    ...rotation && {
                        rotation
                    },
                    scale: scale,
                    src: getEncodedSvg(svg)
                })
            });
        } else if (OlStyleUtil.getIsFontGlyphBased(markSymbolizer)) {
            const strokeRgbaColor = (
                strokeColor && OlStyleUtil.checkOpacity(strokeOpacity)
                    ? OlStyleUtil.getRgbaColor(strokeColor, strokeOpacity)
                    : strokeColor
            );
            const fillRgbaColor = (
                fillColor && OlStyleUtil.checkOpacity(fillOpacity)
                    ? OlStyleUtil.getRgbaColor(fillColor, fillOpacity)
                    : fillColor
            );
            const stroke = new this.OlStyleStrokeConstructor({
                ...strokeRgbaColor && {
                    color: strokeRgbaColor
                },
                ...strokeWidth && {
                    width: strokeWidth
                }
            });
            const fill = new this.OlStyleFillConstructor({
                ...fillRgbaColor && {
                    color: fillRgbaColor
                }
            });
            olStyle = new this.OlStyleConstructor({
                text: new this.OlStyleTextConstructor({
                    text: OlStyleUtil.getCharacterForMarkSymbolizer(markSymbolizer),
                    font: OlStyleUtil.getTextFontForMarkSymbolizer(markSymbolizer),
                    ...fill && {
                        fill
                    },
                    ...displacement && {
                        offsetX: displacement[0]
                    },
                    ...displacement && {
                        offsetY: displacement[1]
                    },
                    ...stroke && {
                        stroke
                    },
                    ...rotation && {
                        rotation
                    }
                })
            });
        } else {
            throw new Error('MarkSymbolizer cannot be parsed. Unsupported WellKnownName.');
        }
        return olStyle;
    }
    /**
     * Get the OL Style object  from an GeoStyler-Style IconSymbolizer.
     *
     * @param symbolizer  A GeoStyler-Style IconSymbolizer.
     * @return The OL Style object
     */
    getOlIconSymbolizerFromIconSymbolizer(symbolizer, feat, o, props) {
        for (const key of Object.keys(symbolizer)) {
            if (isGeoStylerFunction(symbolizer[key])) {
                symbolizer[key] = OlStyleUtil.evaluateFunction(symbolizer[key], feat);
            }
        }

        var myrotate = symbolizer.rotate;
        if (isNaN(symbolizer.rotate)) {
            myrotate = props
                ? props[symbolizer.rotate + '_asrotate']
                : symbolizer.rotate;
            if (props) {
                if (props[symbolizer.rotate + '_asrotate']) {
                    myrotate = props[symbolizer.rotate + '_asrotate'];
                } else if (props[symbolizer.rotate + '_value']) {
                    myrotate = props[symbolizer.rotate + '_value'];
                }
            }
        }

        symbolizer.size = o
            ? parseFloat(o) * symbolizer.size
            : symbolizer.size;

        const baseProps = {
            src: isSprite(symbolizer.image)
                ? symbolizer.image.source
                : symbolizer.image,
            crossOrigin: 'anonymous',
            opacity: symbolizer.opacity,
            width: symbolizer.size,
            // Rotation in openlayers is radians while we use degree
            rotation: (
                parseFloat(myrotate)
                    ? parseFloat(myrotate) * DEGREES_TO_RADIANS
                    : undefined
            ),
            displacement: symbolizer.offset,
            size: isSprite(symbolizer.image)
                ? symbolizer.image.size
                : undefined,
            offset: isSprite(symbolizer.image)
                ? symbolizer.image.position
                : undefined
        };

        // check if IconSymbolizer.image contains a placeholder
        const prefix = '\\{\\{';
        const suffix = '\\}\\}';
        const regExp = new RegExp(prefix + '.*?' + suffix, 'g');
        const regExpRes = typeof(symbolizer.image) === 'string'
            ? symbolizer
                .image
                .match(regExp)
            : null;
        if (regExpRes) {
            // if it contains a placeholder return olStyleFunction
            const olPointStyledIconFn = (feature) => {
                let src = OlStyleUtil.resolveAttributeTemplate(feature, symbolizer.image, '');
                // src can't be blank, would trigger ol errors
                if (!src) {
                    src = symbolizer.image + '';
                }
                let image;
                if (this.olIconStyleCache[src]) {
                    image = this.olIconStyleCache[src];
                    if (baseProps.rotation !== undefined) {
                        image.setRotation(baseProps.rotation);
                    }
                    if (baseProps.opacity !== undefined) {
                        image.setOpacity(baseProps.opacity);
                    }
                } else {
                    image = new this.OlStyleIconConstructor({
                        ...baseProps,
                        src // order is important
                    });
                    this.olIconStyleCache[src] = image;
                }
                const style = new this.OlStyleConstructor({image});
                return style;
            };
            return olPointStyledIconFn;
        } else {
            return new this.OlStyleConstructor({
                image: new this.OlStyleIconConstructor({
                    ...baseProps
                })
            });
        }
    }
    /**
     * Get the OL Style object from an GeoStyler-Style LineSymbolizer.
     *
     * @param symbolizer A GeoStyler-Style LineSymbolizer.
     * @return The OL Style object
     */
    getOlLineSymbolizerFromLineSymbolizer(symbolizer, feat, o, props) {
        for (const key of Object.keys(symbolizer)) {
            if (isGeoStylerFunction(symbolizer[key])) {
                symbolizer[key] = OlStyleUtil.evaluateFunction(symbolizer[key], feat);
            }
        }

        if (o) {
            symbolizer.width = parseFloat(o) * symbolizer.width;
            symbolizer.dashOffset = parseFloat(o) * symbolizer.dashOffset;
            if (symbolizer.offset) {
                symbolizer.offset = parseFloat(o) * symbolizer.offset;
            } else {
                symbolizer.offset = null;
            }
        }
        var myDashArray;
        if (symbolizer.dasharray && symbolizer.dasharray != '' && o) {
            myDashArray = symbolizer
                .dasharray
                .map((value) => {
                    return parseFloat(value) * o
                });
        } else if (symbolizer.dasharray && symbolizer.dasharray != '') {
            myDashArray = symbolizer
                .dasharray
                .map((value) => {
                    return parseFloat(value)
                });
        }

        const color = symbolizer.color;
        const opacity = symbolizer.opacity;
        const sColor = (color && opacity !== null && opacity !== undefined)
            ? OlStyleUtil.getRgbaColor(color, opacity)
            : color;
        return new this.OlStyleConstructor({
            stroke: new this.OlStyleStrokeConstructor({
                color: sColor,
                width: symbolizer.width,
                lineCap: symbolizer.cap,
                lineJoin: symbolizer.join,
                lineDash: myDashArray,
                lineDashOffset: symbolizer.dashOffset,
                offset: symbolizer.offset
            })
        });
    }
    /**
     * Get the OL Style object from an GeoStyler-Style FillSymbolizer.
     *
     * @param symbolizer A GeoStyler-Style FillSymbolizer.
     * @return The OL Style object
     */
    getOlPolygonSymbolizerFromFillSymbolizer(symbolizer, feat, o, props) {
        for (const key of Object.keys(symbolizer)) {
            if (isGeoStylerFunction(symbolizer[key])) {
                symbolizer[key] = OlStyleUtil.evaluateFunction(symbolizer[key], feat);
            }
        }

        if (o) {
            symbolizer.outlineWidth = parseFloat(o) * symbolizer.outlineWidth;
        }
        var myDashArray;
        if (symbolizer.outlineDasharray && symbolizer.outlineDasharray != '' && o) {
            myDashArray = symbolizer
                .outlineDasharray
                .map((value) => {
                    return parseFloat(value) * o
                });
        } else if (symbolizer.outlineDasharray && symbolizer.outlineDasharray != '') {
            myDashArray = symbolizer
                .outlineDasharray
                .map((value) => {
                    return parseFloat(value)
                });
        }

        const color = symbolizer.color;
        const opacity = symbolizer.opacity ?? symbolizer.fillOpacity;
        const fColor = color && Number.isFinite(opacity)
            ? OlStyleUtil.getRgbaColor(color, opacity)
            : color;
        let fill = color
            ? new this.OlStyleFillConstructor({color: fColor})
            : undefined;
        const outlineColor = symbolizer.outlineColor;
        const outlineOpacity = symbolizer.outlineOpacity;
        const oColor = (outlineColor && Number.isFinite(outlineOpacity))
            ? OlStyleUtil.getRgbaColor(outlineColor, outlineOpacity)
            : outlineColor;
        const stroke = outlineColor || symbolizer.outlineWidth
            ? new this.OlStyleStrokeConstructor(
                {color: oColor, width: symbolizer.outlineWidth, lineDash: myDashArray}
            )
            : undefined;
        const olStyle = new OlStyle({fill, stroke});
        if (symbolizer.graphicFill) {

            if (symbolizer.graphicFill.kind === 'Mark') {
                var tb = symbolizer['graphicFill'];

                if (o) {
                    tb.scale = parseFloat(o) * tb.scale;
                }

                if (tb.wellKnownName.includes('shape://')) {
                    var dee = tb
                        .wellKnownName
                        .slice(8);
                    tb.wellKnownName = dee;
                }

                switch (tb.wellKnownName) {
                    case 'horline':
                        tb.wellKnownName = 'hatch';
                        break;

                }

                if (!symbolizer.color) {
                    symbolizer.color = '#000000';
                    symbolizer.opacity = 0;
                }
                if (!tb.color) {
                    tb.color = '#000000';
                }

                fill = new OlStyleFillPattern({
                    pattern: (tb.wellKnownName != 'Image (PNG)')
                        ? tb.wellKnownName
                        : undefined,
                    image: (tb.wellKnownName == 'Image (PNG)')
                        ? new OlStyleIcon({src: tb.imgFile})
                        : undefined,
                    ratio: 1,
                    icon: tb.wellKnownName == 'Image (PNG)'
                        ? new OlStyleIcon({src: 'data/target.png'})
                        : undefined,
                    color: tb.color && tb.opacity
                        ? OlStyleUtil.getRgbaColor(tb.color, parseFloat(tb.opacity))
                        : tb.color,
                    opacity: tb.opacity
                        ? parseFloat(tb.opacity)
                        : 0,
                    offset: tb.offset
                        ? parseFloat(tb.offset)
                        : 0,
                    scale: tb.scale
                        ? parseFloat(tb.scale)
                        : 1,
                    fill: new this.OlStyleFillConstructor({
                        color: symbolizer.color && symbolizer.opacity
                            ? OlStyleUtil.getRgbaColor(symbolizer.color, parseFloat(symbolizer.opacity))
                            : symbolizer.color
                    }),
                    size: tb.radius
                        ? parseFloat(tb.radius)
                        : 3,
                    spacing: tb.spacing
                        ? parseFloat(tb.spacing)
                        : 10,
                    angle: tb.rotate
                        ? parseFloat(tb.rotate)
                        : 0
                });
            } else if (symbolizer.graphicFill.kind === 'Icon') {
                var tb = symbolizer['graphicFill'];
                if (tb.wellKnownName.includes('shape://')) {
                    var dee = tb
                        .wellKnownName
                        .slice(8);
                    tb.wellKnownName = dee;
                }

                switch (tb.wellKnownName) {
                    case 'horline':
                        tb.wellKnownName = 'hatch';
                        break;

                }

                if (!symbolizer.color) {
                    symbolizer.color = '#000000';
                    symbolizer.opacity = 0;
                }
                if (!tb.color) {
                    tb.color = '#000000';
                }

                if (o) {
                    tb.scale = parseFloat(o) * tb.scale;
                }

                fill = new OlStyleFillPattern({
                    pattern: undefined,
                    image: new OlStyleIcon({src: tb.imgFile}),
                    ratio: 1,
                    icon: new OlStyleIcon({src: tb.imgFile}),
                    color: tb.color && tb.opacity
                        ? OlStyleUtil.getRgbaColor(tb.color, parseFloat(tb.opacity))
                        : tb.color,
                    opacity: tb.opacity
                        ? parseFloat(tb.opacity)
                        : 0,
                    offset: tb.offset
                        ? parseFloat(tb.offset)
                        : 0,
                    scale: tb.scale
                        ? parseFloat(tb.scale)
                        : 1,
                    fill: new this.OlStyleFillConstructor({
                        color: symbolizer.color && symbolizer.opacity
                            ? OlStyleUtil.getRgbaColor(symbolizer.color, parseFloat(symbolizer.opacity))
                            : symbolizer.color
                    }),
                    size: tb.radius
                        ? parseFloat(tb.radius)
                        : 3,
                    spacing: tb.spacing
                        ? parseFloat(tb.spacing)
                        : 10,
                    angle: tb.rotate
                        ? parseFloat(tb.rotate)
                        : 0
                });
            } else {
                const pattern = this.getOlPatternFromGraphicFill(symbolizer.graphicFill);
                if (!fill) {
                    fill = new OlStyleFill({});
                }
                if (pattern) {
                    fill.setColor(pattern);
                }
            }
            olStyle.setFill(fill);
        }
        return olStyle;
    }
    /**
     * Get the OL Style object from an GeoStyler-Style FillSymbolizer.
     *
     * @param symbolizer A GeoStyler-Style FillSymbolizer.
     * @return The OL Style object
     */
    getOlPolygonSymbolizerFromFillPatternSymbolizer(symbolizer, feat, o, props) {
        for (const key of Object.keys(symbolizer)) {
            if (isGeoStylerFunction(symbolizer[key])) {
                symbolizer[key] = OlStyleUtil.evaluateFunction(symbolizer[key], feat);
            }
        }

        if (o) {
            symbolizer.outlineWidth = parseFloat(o) * symbolizer.outlineWidth;
            symbolizer.scale = parseFloat(o) * symbolizer.scale;
            symbolizer.size = parseFloat(o) * symbolizer.size;
            symbolizer.spacing = parseFloat(o) * symbolizer.spacing;
        }
        var myDashArray;
        if (symbolizer.outlineDasharray && symbolizer.outlineDasharray != '' && o) {
            myDashArray = symbolizer
                .outlineDasharray
                .map((value) => {
                    return parseFloat(value) * o
                });
        } else if (symbolizer.outlineDasharray && symbolizer.outlineDasharray != '') {
            myDashArray = symbolizer
                .outlineDasharray
                .map((value) => {
                    return parseFloat(value)
                });
        }

        const color = symbolizer.color;
        const opacity = symbolizer.opacity ?? symbolizer.fillOpacity;
        const fColor = color && Number.isFinite(opacity)
            ? OlStyleUtil.getRgbaColor(color, opacity)
            : color;
        let fill = color
            ? new this.OlStyleFillConstructor({color: fColor})
            : undefined;
        const outlineColor = symbolizer.outlineColor;
        const outlineOpacity = symbolizer.outlineOpacity;
        const oColor = (outlineColor && Number.isFinite(outlineOpacity))
            ? OlStyleUtil.getRgbaColor(outlineColor, outlineOpacity)
            : outlineColor;
        const stroke = outlineColor || symbolizer.outlineWidth
            ? new this.OlStyleStrokeConstructor(
                {color: oColor, width: symbolizer.outlineWidth, lineDash: myDashArray}
            )
            : undefined;

        const bcolor = symbolizer.bgcolor;
        const bopacity = symbolizer.bgopacity;
        const fbColor = bcolor && Number.isFinite(bopacity)
            ? OlStyleUtil.getRgbaColor(bcolor, bopacity)
            : bcolor;

        fill = new OlStyleFillPattern({
            pattern: (symbolizer.pattern != 'Image (PNG)')
                ? symbolizer.pattern
                : undefined,
            image: (symbolizer.pattern == 'Image (PNG)')
                ? new OlStyleIcon({src: symbolizer.imgFile})
                : undefined,
            ratio: 1,
            icon: symbolizer.pattern == 'Image (PNG)'
                ? new OlStyleIcon({src: 'data/target.png'})
                : undefined,
            color: fColor,
            opacity: symbolizer.opacity
                ? parseFloat(symbolizer.opacity)
                : 0,
            offset: symbolizer.offset,
            scale: parseFloat(symbolizer.scale),
            fill: new this.OlStyleFillConstructor({color: fbColor}),
            size: parseFloat(symbolizer.size),
            spacing: parseFloat(symbolizer.spacing),
            angle: parseFloat(symbolizer.angle)
        });

        const olStyle = new OlStyle({fill, stroke});
        return olStyle;
    }
    /**
     * Get the pattern for a graphicFill.
     *
     * This creates a CanvasPattern based on the
     * properties of the given PointSymbolizer. Currently,
     * only IconSymbolizer and MarkSymbolizer are supported.
     *
     * @param graphicFill The Symbolizer that holds the pattern config.
     * @returns The created CanvasPattern, or null.
     */
    getOlPatternFromGraphicFill(graphicFill) {
        if (!isIconSymbolizer(graphicFill) && !isMarkSymbolizer(graphicFill)) {
            return null;
        }
        // Temporary canvas.
        // TODO: Can/should we reuse an pre-existing one for efficiency?
        const tmpCanvas = document.createElement('canvas');
        const tmpContext = tmpCanvas.getContext('2d');
        let canvasSize;
        let iconSize = [8, 8];
        let iconSpacing = 1.5;
        let scaleFactor = 1;
        if (isIconSymbolizer(graphicFill)) {
            const graphicFillStyle = this.getOlIconSymbolizerFromIconSymbolizer(
                graphicFill
            );
            const graphicFillImage = graphicFillStyle
                ?.getImage();
            graphicFillImage
                ?.load(); // Needed for Icon type images with a remote src
            // We can only work with the image once it's loaded
            if (
                graphicFillImage
                    ?.getImageState() !== OlImageState.LOADED
            ) {
                return null;
            }
            iconSize = graphicFillStyle
                .getImage()
                .getSize();
            canvasSize = iconSize.map(item => item * iconSpacing);
            tmpCanvas.width = canvasSize[0];
            tmpCanvas.height = canvasSize[1];
            // Create the context where we'll be drawing the style on
            const vectorContext = toContext(tmpContext, {
                size: canvasSize,
                pixelRatio: 1
            });
            const pointCoords = canvasSize.map(item => item / 2);
            const pointFeature = new OlFeature(new OlGeomPoint(pointCoords));
            vectorContext.drawFeature(pointFeature, graphicFillStyle);
            return tmpContext.createPattern(tmpCanvas, 'repeat');
        }

        const svgPattern = fillPatternSvg(graphicFill);
        const isLineSymbol = LINE_WELLKNOWNNAMES.includes(
            String(graphicFill.wellKnownName)
        );
        const cleanedWellKnownName = cleanWellKnownName(graphicFill.wellKnownName);
        const isSimplePatternFill = !graphicFill.strokeWidth && NOFILL_WELLKNOWNNAMES.includes(
            cleanedWellKnownName
        );
        const isDenseFill = cleanedWellKnownName.startsWith('dense');
        const iconRotation = graphicFill.rotate || 0;
        // Hack to try to join lines for hatch patterns, but space out icon patterns.
        // Diagonal lines still do not render nicely in the corners, due to tiling.
        // TODO: Maybe use VendorOption's to control spacing?
        if (isLineSymbol || isSimplePatternFill || isDenseFill) {
            // Extend lines that aren't horizontal or vertical to be full size of the canvas
            const isNotVerticalOrHorizontal = (iconRotation / DEGREES_TO_RADIANS) % 1 !== 0;
            if (isNotVerticalOrHorizontal) {
                scaleFactor = Math.sin(iconRotation * DEGREES_TO_RADIANS);
            }
            iconSpacing = 1;
        }
        const diameter = (graphicFill.radius ?? 4) * 2;
        iconSize = [diameter, diameter];
        canvasSize = iconSize.map(item => item * iconSpacing * scaleFactor);
        tmpCanvas.width = canvasSize[0];
        tmpCanvas.height = canvasSize[1];
        drawSvgToCanvas(svgPattern, tmpContext, canvasSize[0], iconRotation);
        return tmpContext.createPattern(tmpCanvas, 'repeat');
    }

    /**
     * Get the OL StyleFunction object from an GeoStyler-Style TextSymbolizer.
     *
     * @param {TextSymbolizer} textSymbolizer A GeoStyler-Style TextSymbolizer.
     * @return {object} The OL StyleFunction
     */
    getOlTextSymbolizerFromTextSymbolizer(symbolizer, feat, o, props) {
        for (const key of Object.keys(symbolizer)) {
            if (isGeoStylerFunction(symbolizer[key])) {
                symbolizer[key] = OlStyleUtil.evaluateFunction(symbolizer[key], feat);
            }
        }
        const color = symbolizer.color;
        let placement = symbolizer.placement;

        if (!placement) {
            // When setting placement it must not be undefined. So we set it to the OL
            // default value.
            placement = 'point';
        }
        if (placement === 'line-center') {
            // line-center not supported by OL. So we use the closest supported value.
            placement = 'line';
        }

        var allowOverlap = symbolizer.overflow != null
            ? symbolizer.overflow == 'true'
            : symbolizer.allowOverlap != null
                ? symbolizer.allowOverlap == 'true'
                : false;

        if (typeof symbolizer.font === "string") {
            symbolizer.font = [symbolizer.font];
        } else if (!Array.isArray(symbolizer.font)) {
            symbolizer.font = [];
        }

        if (o) {
            symbolizer.offset = symbolizer.offset || [0, 0];
            symbolizer.offset[0] = parseFloat(o) * symbolizer.offset[0];
            symbolizer.offset[1] = parseFloat(o) * symbolizer.offset[1];
            symbolizer.haloWidth = parseFloat(o) * symbolizer.haloWidth;
        }

        var myrotate = symbolizer.rotate;
        if (isNaN(symbolizer.rotate)) {
            myrotate = props
                ? props[symbolizer.rotate + '_asrotate']
                : symbolizer.rotate;
            if (props) {
                if (props[symbolizer.rotate + '_asrotate']) {
                    myrotate = props[symbolizer.rotate + '_asrotate'];
                } else if (props[symbolizer.rotate + '_value']) {
                    myrotate = props[symbolizer.rotate + '_value'];
                }
            }
            try {
                myrotate = parseFloat(myrotate);
                if (isNaN(myrotate)) {
                    myrotate = 0;
                }
            } catch (e) {
                myrotate = 0;
            }
            placement = 'point';
        } else if (symbolizer.rotate) {
            myrotate = parseFloat(symbolizer.rotate);
            if (myrotate == 0) {
                placement = 'line';
            } else {
                placement = 'point';
            }
        }

        const opacity = symbolizer.opacity
            ? parseFloat(symbolizer.opacity)
            : 1;
        const fColor = color && Number.isFinite(opacity)
            ? OlStyleUtil.getRgbaColor(color, opacity)
            : color;
        const haloColor = symbolizer.haloColor;
        const haloWidth = symbolizer.haloWidth
            ? parseFloat(symbolizer.haloWidth)
            : 0;
        const haloOpacity = symbolizer.haloOpacity
            ? parseFloat(symbolizer.haloOpacity)
            : 1;
        const sColor = haloColor && Number.isFinite(haloOpacity)
            ? OlStyleUtil.getRgbaColor(haloColor, haloOpacity)
            : haloColor;
        const baseProps = {
            font: getTextFont(symbolizer, o),
            fill: new this.OlStyleFillConstructor({color: fColor}),
            stroke: new this.OlStyleStrokeConstructor({
                color: sColor,
                width: haloWidth
                    ? haloWidth
                    : 0
            }),
            overflow: allowOverlap,
            offsetX: (
                symbolizer.offset
                    ? symbolizer.offset[0]
                    : 0
            ),
            offsetY: (
                symbolizer.offset
                    ? symbolizer.offset[1]
                    : 0
            ),
            rotation: typeof(myrotate) === 'number'
                ? myrotate * DEGREES_TO_RADIANS
                : undefined,
            placement: placement,
            // TODO check why props match
            textAlign: symbolizer.textAlign,
            textBaseline: symbolizer.textBaseline,
            declutterMode: symbolizer.declutterMode
        };

        // check if TextSymbolizer.label contains a placeholder
        const prefix = '\\{\\{';
        const suffix = '\\}\\}';
        const regExp = new RegExp(prefix + '.*?' + suffix, 'g');
        let regExpRes;
        if (!isGeoStylerStringFunction(symbolizer.label)) {
            regExpRes = symbolizer.label
                ? symbolizer
                    .label
                    .match(regExp)
                : null;
        }
        if (regExpRes) {
            // if it contains a placeholder return olStyleFunction
            const olPointStyledLabelFn = (feature) => {
                const text = new this.OlStyleTextConstructor({
                    text: resolveAttributeTemplate(feature, symbolizer.label, ''),
                    ...baseProps
                });

                const style = new this.OlStyleConstructor({text: text});
                return style;
            };
            return olPointStyledLabelFn;
        } else {
            // if TextSymbolizer does not contain a placeholder return OlStyle
            return new this.OlStyleConstructor({
                text: new this.OlStyleTextConstructor({
                    text: symbolizer.label,
                    ...baseProps
                })
            });
        }
    }

    writeStyleNow(geoStylerStyle, thismap, renderResolution) {
        const clonedStyle = deepCloneKeepFunctions(geoStylerStyle);
        const olStyle = this.getOlStyleTypeFromGeoStylerStyle(
            clonedStyle,
            thismap,
            renderResolution
        );
        return olStyle;
    }

    writeStyle(geoStylerStyle) {
        return new Promise((resolve) => {
            const clonedStyle = deepCloneKeepFunctions(geoStylerStyle);
            const unsupportedProperties = this.checkForUnsupportedProperties(clonedStyle);
            const olStyle = this.getOlStyleTypeFromGeoStylerStyle(clonedStyle);
            resolve(olStyle);
            // try {     const olStyle = this.getOlStyleTypeFromGeoStylerStyle(clonedStyle);
            // resolve({         output: olStyle,         unsupportedProperties, warnings:
            // unsupportedProperties && ['Your style contains unsupportedProperties!'] }); }
            // catch (error) {     resolve({ errors: [error]     }); }
        });
    }

};
